import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

// Role constants
export const ROLES = {
  ADMIN: 'admin',
  ESTIMATOR: 'estimator',
  VIEWER: 'viewer',
}

// Permissions matrix
const PERMISSIONS = {
  // User Management
  manageUsers: [ROLES?.ADMIN],
  assignRoles: [ROLES?.ADMIN],
  // Financial Settings
  viewFinancials: [ROLES?.ADMIN],
  editFinancials: [ROLES?.ADMIN],
  // Project Data
  createProject: [ROLES?.ADMIN, ROLES?.ESTIMATOR],
  editProject: [ROLES?.ADMIN, ROLES?.ESTIMATOR],
  deleteProject: [ROLES?.ADMIN, ROLES?.ESTIMATOR],
  // Cost Estimation & BOQ
  manageCostEstimation: [ROLES?.ADMIN, ROLES?.ESTIMATOR],
  manageBOQ: [ROLES?.ADMIN, ROLES?.ESTIMATOR],
  manageVendorQuotes: [ROLES?.ADMIN, ROLES?.ESTIMATOR],
  manageRFQ: [ROLES?.ADMIN, ROLES?.ESTIMATOR],
  manageMasterData: [ROLES?.ADMIN, ROLES?.ESTIMATOR],
  // Read-only
  viewDashboards: [ROLES?.ADMIN, ROLES?.ESTIMATOR, ROLES?.VIEWER],
  viewReports: [ROLES?.ADMIN, ROLES?.ESTIMATOR, ROLES?.VIEWER],
  viewProjects: [ROLES?.ADMIN, ROLES?.ESTIMATOR, ROLES?.VIEWER],
}

// Fixed session timeout: 10 minutes of inactivity
const SESSION_TIMEOUT_MS = 10 * 60 * 1000;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [userProfile, setUserProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [profileLoading, setProfileLoading] = useState(true)
  const inactivityTimerRef = useRef(null)
  const currentUserRef = useRef(null)

  // Keep currentUserRef in sync
  useEffect(() => {
    currentUserRef.current = user
  }, [user])

  const clearInactivityTimer = useCallback(() => {
    if (inactivityTimerRef?.current) {
      clearTimeout(inactivityTimerRef?.current)
      inactivityTimerRef.current = null
    }
  }, [])

  const resetInactivityTimer = useCallback(() => {
    clearInactivityTimer()
    if (currentUserRef?.current) {
      inactivityTimerRef.current = setTimeout(async () => {
        if (currentUserRef?.current) {
          await supabase?.auth?.signOut()
        }
      }, SESSION_TIMEOUT_MS)
    }
  }, [clearInactivityTimer])

  // Attach activity listeners when user is logged in
  useEffect(() => {
    if (!user) {
      clearInactivityTimer()
      return
    }

    const activityEvents = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click']
    activityEvents?.forEach(event => window.addEventListener(event, resetInactivityTimer, { passive: true }))
    resetInactivityTimer()

    return () => {
      activityEvents?.forEach(event => window.removeEventListener(event, resetInactivityTimer))
      clearInactivityTimer()
    };
  }, [user, resetInactivityTimer, clearInactivityTimer])

  // Isolated async operations - never called from auth callbacks
  const profileOperations = {
    async load(userId) {
      if (!userId) return
      setProfileLoading(true)
      try {
        const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.single()
        if (!error) setUserProfile(data)
      } catch (error) {
        console.error('Profile load error:', error)
      } finally {
        setProfileLoading(false)
      }
    },

    clear() {
      setUserProfile(null)
      setProfileLoading(false)
    }
  }

  // Auth state handlers - PROTECTED from async modification
  const authStateHandlers = {
    // This handler MUST remain synchronous - Supabase requirement
    onChange: (event, session) => {
      // TOKEN_REFRESHED is a silent background refresh — the user/session identity
      // has not changed, so skip the state update to prevent unnecessary re-renders
      // across the entire app (e.g. Service Calculator losing in-progress work).
      if (event === 'TOKEN_REFRESHED') return;

      setUser(session?.user ?? null)
      setLoading(false)
      
      if (session?.user) {
        setProfileLoading(true) // Set true synchronously before async load
        profileOperations?.load(session?.user?.id) // Fire-and-forget
      } else {
        profileOperations?.clear()
      }
    }
  }

  useEffect(() => {
    // Initial session check
    supabase?.auth?.getSession()?.then(({ data: { session } }) => {
      authStateHandlers?.onChange(null, session)
    })

    // CRITICAL: This must remain synchronous
    const { data: { subscription } } = supabase?.auth?.onAuthStateChange(
      authStateHandlers?.onChange
    )

    return () => subscription?.unsubscribe()
  }, [])

  // Auth methods
  const signIn = async (email, password) => {
    try {
      const { data, error } = await supabase?.auth?.signInWithPassword({ email, password })
      return { data, error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const signOut = async () => {
    try {
      const { error } = await supabase?.auth?.signOut()
      if (!error) {
        setUser(null)
        profileOperations?.clear()
        try { sessionStorage.removeItem('sc_session_state'); } catch (_) {}
      }
      return { error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const signUp = async (email, password) => {
    try {
      const { data, error } = await supabase?.auth?.signUp({ email, password })
      return { data, error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const updateProfile = async (updates) => {
    if (!user) return { error: { message: 'No user logged in' } }
    
    try {
      const { data, error } = await supabase?.from('user_profiles')?.update(updates)?.eq('id', user?.id)?.select()?.single()
      if (!error) setUserProfile(data)
      return { data, error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  // Derived role state
  const userRole = userProfile?.role || ROLES?.VIEWER
  const isAdmin = userRole === ROLES?.ADMIN
  const isEstimator = userRole === ROLES?.ESTIMATOR
  const isViewer = userRole === ROLES?.VIEWER

  // Permission checker
  const hasPermission = (permission) => {
    const allowedRoles = PERMISSIONS?.[permission]
    if (!allowedRoles) return false
    return allowedRoles?.includes(userRole);
  }

  // Convenience permission flags
  const permissions = {
    canManageUsers: hasPermission('manageUsers'),
    canAssignRoles: hasPermission('assignRoles'),
    canViewFinancials: hasPermission('viewFinancials'),
    canEditFinancials: hasPermission('editFinancials'),
    canCreateProject: hasPermission('createProject'),
    canEditProject: hasPermission('editProject'),
    canDeleteProject: hasPermission('deleteProject'),
    canManageCostEstimation: hasPermission('manageCostEstimation'),
    canManageBOQ: hasPermission('manageBOQ'),
    canManageVendorQuotes: hasPermission('manageVendorQuotes'),
    canManageRFQ: hasPermission('manageRFQ'),
    canManageMasterData: hasPermission('manageMasterData'),
    canViewDashboards: hasPermission('viewDashboards'),
    canViewReports: hasPermission('viewReports'),
    canViewProjects: hasPermission('viewProjects'),
  }

  const value = {
    user,
    userProfile,
    loading,
    profileLoading,
    signIn,
    signUp,
    signOut,
    updateProfile,
    isAuthenticated: !!user,
    // Role flags
    userRole,
    isAdmin,
    isEstimator,
    isViewer,
    hasPermission,
    permissions,
    ROLES,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
